/* global globalThis */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";
import {
  Box,
  CssBaseline,
  Button,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  TableBody,
  Stack,
  IconButton,
  Dialog,
  DialogContent,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
  styled,
} from "@mui/material";

import {
  Download as DownloadIcon,
  Calendar as CalendarIcon,
} from "lucide-react";
import {
  Language as LanguageRoundedIcon,
  WatchLater as WatchLaterRoundedIcon,
  ArrowDropUp as ArrowDropUpIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from "@mui/icons-material";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import PageHeader from "../../components/common/PageHeader";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchMemberReporting,
  exportMemberReporting,
} from "../../api/reporting";
import { useLoading } from "../../context/LoadingContext";

// --- Helpers & Small Components ---

const CalendarPickerButton = (props) => (
  <IconButton
    {...props}
    aria-label={props["aria-label"] || "Open date picker"}
    title={props.title || "Open date picker"}
    size="small"
  >
    <CalendarIcon />
  </IconButton>
);

CalendarPickerButton.propTypes = {
  "aria-label": PropTypes.string,
  title: PropTypes.string,
};

const ProgramList = ({ programs }) => {
  if (!programs) {
    return (
      <Box sx={{ color: "#403C6F", fontSize: "14px", fontWeight: 500 }}>
        N/A
      </Box>
    );
  }

  const list = Array.isArray(programs) ? programs : [programs];
  const names = list
    .filter((p) => p !== null && p !== undefined)
    .map((p) => {
      if (typeof p === "object") {
        return p?.name || p?.title || p?.programName || JSON.stringify(p);
      }
      return String(p);
    })
    .filter((n) => n.trim().length > 0);
  if (names.length === 0) {
    return (
      <Box sx={{ color: "#403C6F", fontSize: "14px", fontWeight: 500 }}>
        N/A
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {names.map((n, i) => (
        <Box
          key={`name-${n || ""}-${i}`}
          sx={{
            color: "#403C6F",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: 1.4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {n}
          {i < names.length - 1 ? "," : ""}
        </Box>
      ))}
    </Box>
  );
};

ProgramList.propTypes = {
  programs: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    ),
    PropTypes.string,
    PropTypes.object,
    PropTypes.node,
  ]),
};

const MainContent = styled("div")(() => ({
  flexGrow: 1,
  padding: 0,
  margin: 0,
  width: "100%",
  minHeight: "80vh",
  backgroundColor: "#F7F7FB",
  overflow: "hidden",
}));

function getScreenInches() {
  const { width, height } = globalThis.screen;
  const dpi = globalThis.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

const SECTIONS = {
  members: {
    description: "Search and manage member information",
  },
  providers: {
    title: "Providers",
    description: "Search and manage provider information",
  },
  pharmacies: {
    title: "Pharmacies",
    description: "Search and manage pharmacy information",
  },
  programs: {
    title: "Programs",
    description: "Search and manage program information",
  },
};

const BREADCRUMBS = [
  { label: "Reporting", path: "/reporting/member-reporting" },
  {
    label: "Member Reporting",
    path: "/reporting/member-reporting",
  },
];

const formatClientName = (raw) => {
  if (!raw || typeof raw !== "string") return "N/A";
  let s = raw.trim().replaceAll(/\s+/g, " ");
  // Replace all whitespace-comma-whitespace with ", " using split/join to avoid ReDoS
  s = s
    .split(",")
    .map((part) => part.trim())
    .join(", ");
  s = s.replace(/,\s*Inc\.?$/i, ", Inc");
  const acronymSet = new Set(["LLC", "LP", "LLP"]);
  s = s
    .split(/(\s+)/)
    .map((token) => {
      if (/^\s+$/.test(token)) return token;
      return token
        .split(/(,)/)
        .map((sub) => {
          if (sub === ",") return sub;
          const upper = sub.toUpperCase();
          if (acronymSet.has(upper)) return upper;
          return upper.charAt(0) + upper.slice(1).toLowerCase();
        })
        .join("");
    })
    .join("");
  return s;
};

const mapResponse = (apiRes) => {
  if (!apiRes) return [];
  const rows = apiRes.content || apiRes;
  if (!Array.isArray(rows)) return [];

  return rows.map((r, idx) => ({
    id: `${r.memberId || idx}`,
    memberId: r.memberId || "N/A",
    memberName: r.memberName || "N/A",
    dob: r.dateOfBirth || "N/A",
    average: r.averageReachRate ?? "N/A",
    attempts: r.numberOfAttempts ?? "N/A",
    programs: r.enrolledPrograms || "N/A",
    pdcEnrolled: r.pdcEnrolled || "N/A",
    language: r.language || "N/A",
    timezone: r.timezone || "N/A",
    clientDetails: r.clientDetails || "N/A",
    relationshipCode: r.relationshipCode || "N/A",
    clientName: formatClientName(r.clientDetails || ""),
  }));
};

const compareMemberIds = (av, bv) => {
  const isNumeric = (val) => /^\d+$/.test(val);
  const isAlphaNum = (val) => /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]+$/.test(val);
  const isAlpha = (val) => /^[a-zA-Z]+$/.test(val);

  const getType = (val) => {
    if (isNumeric(val)) return 0;
    if (isAlphaNum(val)) return 1;
    if (isAlpha(val)) return 2;
    return 3;
  };

  const aType = getType(av);
  const bType = getType(bv);

  if (aType !== bType) {
    return aType - bType;
  }

  if (aType === 0) {
    return Number.parseInt(av, 10) - Number.parseInt(bv, 10);
  }
  return String(av ?? "").localeCompare(String(bv ?? ""));
};

const compareDates = (av, bv) => {
  const ad = av && av !== "N/A" ? new Date(av) : new Date(8640000000000000);
  const bd = bv && bv !== "N/A" ? new Date(bv) : new Date(8640000000000000);
  return ad - bd;
};

const getSortedData = (data, sortConfig) => {
  const sorted = [...data];
  const { key, direction } = sortConfig;
  const compare = (a, b) => {
    const av = a[key];
    const bv = b[key];

    if (key === "dob") {
      return compareDates(av, bv);
    }

    if (key === "memberId") {
      return compareMemberIds(av, bv);
    }

    const an = typeof av === "number" ? av : Number.parseFloat(av);
    const bn = typeof bv === "number" ? bv : Number.parseFloat(bv);
    const bothNumeric = !Number.isNaN(an) && !Number.isNaN(bn);
    if (bothNumeric) {
      return an - bn;
    }
    return String(av ?? "").localeCompare(String(bv ?? ""));
  };
  sorted.sort((a, b) => (direction === "asc" ? compare(a, b) : -compare(a, b)));
  return sorted;
};

// --- Extracted Components ---

const MemberExportButton = ({ totalRows, appliedDateRange, setError }) => {
  const handleExport = async () => {
    if (totalRows === 0) return;
    try {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const useCustom = appliedDateRange.startDate && appliedDateRange.endDate;
      const params = {
        startDate: format(
          useCustom ? appliedDateRange.startDate : startOfYear,
          "yyyy-MM-dd",
        ),
        endDate: format(
          useCustom ? appliedDateRange.endDate : today,
          "yyyy-MM-dd",
        ),
      };
      const blob = await exportMemberReporting(params);

      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement("a");

      const formatMDY = (dateStr) => {
        const d = new Date(dateStr);
        return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(
          d.getDate(),
        ).padStart(2, "0")}/${d.getFullYear()}`;
      };
      const rangeLabel = `(${formatMDY(params.startDate)})-(${formatMDY(
        params.endDate,
      )})`;
      a.href = url;
      a.download = `Member_Reporting_${rangeLabel}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export failed", e);
      setError("Failed to export data");
    }
  };

  const hasData = totalRows > 0;
  const commonColor = hasData ? "#403C6F" : "#999893";

  return (
    <Button
      startIcon={
        <DownloadIcon
          style={{ width: "18px", height: "16px", color: commonColor }}
        />
      }
      onClick={handleExport}
      disabled={!hasData}
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "24px",
        letterSpacing: "0%",
        borderRadius: "20px",
        border: "1px solid #E8E8E9",
        textTransform: "none",
        backgroundColor: hasData ? "#FFFFFF" : "#EFEFEF",
        color: commonColor,
        px: 2,
        py: 0,
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
      }}
    >
      Export Data
    </Button>
  );
};

MemberExportButton.propTypes = {
  totalRows: PropTypes.number.isRequired,
  appliedDateRange: PropTypes.object.isRequired,
  setError: PropTypes.func.isRequired,
};

const MemberDateFilterControl = ({
  appliedDateRange,
  openDateDialog,
  closeDateDialog,
  setAppliedDateRange,
  setDateRange,
  setPageIndex,
  totalRows,
}) => {
  const hasDateRange = Boolean(
    appliedDateRange.startDate && appliedDateRange.endDate,
  );

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAppliedDateRange({ startDate: null, endDate: null });
    setDateRange({ startDate: null, endDate: null });
    setPageIndex(0);
    closeDateDialog();
  };

  const handleClick = (e) => {
    const clearIcon = e.target.closest('[data-clear-icon="true"]');
    if (hasDateRange && clearIcon) {
      handleClear(e);
      return;
    }
    openDateDialog();
  };

  const formatDate = (date) => new Date(date).toLocaleDateString("en-US");

  const labelText = hasDateRange
    ? `${formatDate(appliedDateRange.startDate)} - ${formatDate(
        appliedDateRange.endDate,
      )}`
    : "Select Date Range";

  const ariaLabel = hasDateRange
    ? `${labelText}, click X icon to clear`
    : labelText;

  const iconColor = totalRows > 0 ? "#403C6F" : "#999893";
  const iconStyle = { width: "18px", height: "16px", color: iconColor };

  const ClearButton = (
    <Box
      component="span"
      data-clear-icon="true"
      onClick={handleClear}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        ml: 0.5,
        color: iconColor,
        cursor: totalRows > 0 ? "pointer" : "not-allowed",
        opacity: totalRows > 0 ? 1 : 0.5,
        "&:hover": { opacity: totalRows > 0 ? 0.7 : 0.5 },
      }}
      aria-hidden="true"
    >
      <CloseIcon style={iconStyle} />
    </Box>
  );

  return (
    <Button
      variant="outlined"
      endIcon={hasDateRange ? ClearButton : <CalendarIcon style={iconStyle} />}
      onClick={handleClick}
      aria-label={ariaLabel}
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        border: "1px solid #E8E8E9",
        lineHeight: "24px",
        letterSpacing: "0%",
        borderRadius: "20px",
        textTransform: "none",
        color: "#403C6F",
        backgroundColor: "#FFFFFF",
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
      }}
    >
      {labelText}
    </Button>
  );
};

MemberDateFilterControl.propTypes = {
  appliedDateRange: PropTypes.object.isRequired,
  openDateDialog: PropTypes.func.isRequired,
  closeDateDialog: PropTypes.func.isRequired,
  setAppliedDateRange: PropTypes.func.isRequired,
  setDateRange: PropTypes.func.isRequired,
  setPageIndex: PropTypes.func.isRequired,
  totalRows: PropTypes.number.isRequired,
};

const MemberDateRangeDialog = ({
  open,
  onClose,
  dateRange,
  onDateChange,
  endDateError,
  setEndDateError,
  onApply,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="date-range-dialog-title"
      slotProps={{
        paper: {
          sx: { borderRadius: "16px", padding: "16px", minWidth: 400 },
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          id="date-range-dialog-title"
          variant="h6"
          sx={{ color: "#3D3769", fontWeight: 600 }}
        >
          Select Date Range
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close date range dialog"
        >
          <CloseIcon sx={{ color: "#3D3769" }} />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            display="flex"
            gap={2}
            mb={3}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <Box flex={1}>
              <Typography
                variant="body2"
                sx={{
                  color: "#3D3769",
                  mb: 1,
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                Start Date
              </Typography>
              <DesktopDatePicker
                value={dateRange.startDate}
                disableFuture
                onChange={(date) => {
                  onDateChange(date, "startDate");
                  setEndDateError(() => {
                    if (date && dateRange.endDate && dateRange.endDate < date) {
                      return "End date cannot be earlier than start date";
                    }
                    return "";
                  });
                }}
                slots={{ openPickerButton: CalendarPickerButton }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "YYYY-MM-DD",
                    slotProps: {
                      input: {
                        sx: {
                          height: "40px",
                          "& input": {
                            padding: "8px 14px",
                            color: dateRange.startDate ? "#111827" : "#6B7280",
                            "&::placeholder": { color: "#6B7280", opacity: 1 },
                          },
                        },
                      },
                    },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": { borderColor: "#E8E8E9" },
                        "&:hover fieldset": { borderColor: "#D0D0D0" },
                        "&.Mui-focused fieldset": { borderColor: "#E8E8E9" },
                      },
                    },
                  },
                }}
              />
            </Box>
            <Box flex={1}>
              <Typography
                variant="body2"
                sx={{
                  color: "#3D3769",
                  mb: 1,
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                End Date
              </Typography>
              <DesktopDatePicker
                value={dateRange.endDate}
                disableFuture
                minDate={dateRange.startDate || undefined}
                onChange={(date) => {
                  onDateChange(date, "endDate");
                  if (
                    dateRange.startDate &&
                    date &&
                    date < dateRange.startDate
                  ) {
                    setEndDateError(
                      "End date cannot be earlier than start date",
                    );
                  } else {
                    setEndDateError("");
                  }
                }}
                slots={{ openPickerButton: CalendarPickerButton }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "YYYY-MM-DD",
                    error: Boolean(endDateError),
                    helperText: endDateError || "",
                    slotProps: {
                      input: {
                        sx: {
                          height: "40px",
                          "& input": {
                            padding: "8px 14px",
                            color: dateRange.endDate ? "#111827" : "#6B7280",
                            "&::placeholder": { color: "#6B7280", opacity: 1 },
                          },
                        },
                      },
                    },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFFFFF",
                        "& fieldset": { borderColor: "#E8E8E9" },
                        "&:hover fieldset": { borderColor: "#D0D0D0" },
                        "&.Mui-focused fieldset": { borderColor: "#E8E8E9" },
                      },
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </LocalizationProvider>
        <Box display="flex" justifyContent="flex-start" mt={2}>
          <Button
            variant="contained"
            onClick={onApply}
            disabled={
              !(dateRange.startDate && dateRange.endDate) ||
              Boolean(endDateError)
            }
            sx={{
              backgroundColor: "#3D3769",
              borderRadius: "24px",
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1,
              fontSize: "14px",
              "&:hover": { backgroundColor: "#322E59", boxShadow: "none" },
              "&.Mui-disabled": {
                backgroundColor: "#E8E8E9",
                color: "#A0A0A0",
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

MemberDateRangeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dateRange: PropTypes.object.isRequired,
  onDateChange: PropTypes.func.isRequired,
  endDateError: PropTypes.string.isRequired,
  setEndDateError: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};

const MemberReportingTable = ({
  loading,
  error,
  results,
  pageIndex,
  totalRows,
  computedTableHeight,
  sortedMembers,
  sortConfig,
  onSort,
  tableContainerRef,
  isMobile,
}) => {
  if (error) {
    return (
      <p
        style={{
          color: "red",
          backgroundColor: "#ffffff",
          padding: "8px 12px",
          borderRadius: "4px",
          border: "1px solid #ffcccc",
          display: "inline-block",
        }}
      >
        {error}
      </p>
    );
  }

  if (!results.length && (!loading || pageIndex !== 0)) {
    return null;
  }

  const getSortArrows = (colKey) => (
    <span
      style={{ display: "inline-flex", flexDirection: "column", marginLeft: 4 }}
    >
      <ArrowDropUpIcon
        style={{
          fontSize: 16,
          color: "inherit",
          lineHeight: 0,
          opacity:
            sortConfig.key === colKey && sortConfig.direction === "asc"
              ? 1
              : 0.8,
        }}
      />
      <ArrowDropDownIcon
        style={{
          fontSize: 16,
          color: "inherit",
          marginTop: -8,
          lineHeight: 0,
          opacity:
            sortConfig.key === colKey && sortConfig.direction === "desc"
              ? 1
              : 0.8,
        }}
      />
    </span>
  );

  return (
    <div style={{ marginBottom: "16px", marginRight: "0px" }}>
      <TableContainer
        ref={tableContainerRef}
        tabIndex={0}
        component="section"
        aria-label="Member reporting table"
        sx={{
          maxHeight: computedTableHeight || "calc(100dvh - 180px)",
          overflowY: "auto",
          overflowX: "scroll",
          height: computedTableHeight || "calc(100dvh - 180px)",
          backgroundColor: "#fff",
          scrollbarGutter: "stable",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            width: isMobile ? "auto" : "100%",
            minWidth: isMobile ? "1000px" : "auto",
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              color: "#403C6F",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              padding: "16px 20px",
              borderBottom: "none",
              "&:not(.MuiTableCell-head)": {
                fontWeight: 500,
                fontSize: "14px",
              },
            },
            "& .MuiTableHead-root .MuiTableCell-root": {
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "#CFCEDA",
              padding: "10px 14px !important",
              fontStyle: "normal",
              fontWeight: 550,
              fontSize: "16px",
              lineHeight: "24px",
              letterSpacing: "0px",
            },
            "& .MuiTableBody-root .MuiTableRow-root": { height: 90 },
            "& .MuiTableCell-root:first-of-type": {
              width: "13%",
              minWidth: "13%",
              maxWidth: "13%",
            },
            "& .MuiTableCell-root:nth-of-type(2)": {
              width: "15%",
              minWidth: "15%",
              maxWidth: "15%",
            },
            "& .MuiTableCell-root:nth-of-type(3)": {
              width: "12%",
              minWidth: "12%",
              maxWidth: "12%",
            },
            "& .MuiTableCell-root:nth-of-type(4)": {
              width: "16%",
              minWidth: "16%",
              maxWidth: "16%",
            },
            "& .MuiTableCell-root:nth-of-type(5)": {
              width: "13%",
              minWidth: "13%",
              maxWidth: "13%",
            },
            "& .MuiTableCell-root:nth-of-type(6)": {
              width: "16%",
              minWidth: "16%",
              maxWidth: "16%",
            },
            "& .MuiTableCell-root:nth-of-type(7)": {
              width: "20%",
              minWidth: "20%",
              maxWidth: "20%",
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#CFCEDA",
                "& .MuiTableCell-head": { borderRight: "none !important" },
              }}
            >
              {[
                { key: "memberId", label: "Member ID" },
                { key: "memberName", label: "Member Name" },
                { key: "dob", label: "Date Of Birth" },
                {
                  key: "average",
                  label: "Average Reach Rate",
                  tooltip: "Average Reach Rate",
                },
                {
                  key: "attempts",
                  label: "No. Of Attempts",
                  tooltip: "Number Of Attempts",
                  width: 20,
                },
                { key: "programs", label: "Enrolled Programs", width: 20 },
                { key: "clientName", label: "Client Name", width: 20 },
              ].map((col) => (
                <TableCell
                  key={col.key}
                  onClick={() => onSort(col.key)}
                  sx={{
                    cursor: "pointer",
                    color: "#403C6F",
                    fontWeight: 700,
                    fontSize: "14px",
                    width: col.width || "auto",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center" }}>
                    {col.tooltip ? (
                      <Tooltip
                        title={col.tooltip}
                        placement="bottom-start"
                        arrow
                        disableInteractive
                        slotProps={{
                          popper: {
                            sx: {
                              "& .MuiTooltip-tooltip": {
                                backgroundColor: "#1c1c1f",
                                color: "#FFFFFF",
                                margin: 0,
                                fontSize: "12px",
                                lineHeight: 1.3,
                              },
                              "& .MuiTooltip-arrow": {
                                color: "#1c1c1f",
                                margin: 0,
                                left: "50% !important",
                                transform: "translateX(-50%) !important",
                              },
                            },
                          },
                          tooltip: {
                            sx: {
                              margin: 0,
                              marginTop: "0px !important",
                              padding: "4px 8px",
                            },
                          },
                        }}
                      >
                        <div
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            maxWidth: "100%",
                          }}
                        >
                          {col.label}
                        </div>
                      </Tooltip>
                    ) : (
                      col.label
                    )}
                    {getSortArrows(col.key)}
                  </span>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedMembers.map((member) => (
              <TableRow
                key={member.id}
                hover
                sx={{ "&:hover": { backgroundColor: "#F9F9F9 !important" } }}
              >
                <TableCell sx={{ fontWeight: 500 }}>
                  {member.memberId}
                </TableCell>
                <TableCell>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      minWidth: "120px",
                    }}
                  >
                    <Tooltip
                      title={member.memberName || ""}
                      placement="bottom-start"
                      arrow
                      describeChild
                      disableInteractive
                      slotProps={{
                        popper: {
                          sx: {
                            "& .MuiTooltip-tooltip": {
                              backgroundColor: "#1c1c1f",
                              color: "#FFFFFF",
                              margin: 0,
                              fontSize: "12px",
                              lineHeight: 1.3,
                            },
                            "& .MuiTooltip-arrow": {
                              color: "#1c1c1f",
                              margin: 0,
                              left: "50% !important",
                              transform: "translateX(-50%) !important",
                            },
                          },
                        },
                        tooltip: {
                          sx: {
                            margin: 0,
                            marginTop: "0px !important",
                            padding: "4px 8px",
                          },
                        },
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: "16px",
                          lineHeight: "24px",
                          color: "#4b3f72",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "inline-block",
                          maxWidth: "100%",
                        }}
                      >
                        {member.memberName}
                      </span>
                    </Tooltip>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        fontWeight: 500,
                        fontSize: "11px",
                        lineHeight: "150%",
                        color: "#403C6F",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <WatchLaterRoundedIcon
                          style={{ fontSize: 14, color: "#1B2F3F" }}
                        />
                        <span>{member.timezone || "N/A"}</span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <LanguageRoundedIcon
                          style={{ fontSize: 14, color: "#1B2F3F" }}
                        />
                        <Tooltip
                          title={
                            member.language
                              ? member.language.charAt(0).toUpperCase() +
                                member.language.slice(1).toLowerCase()
                              : "English"
                          }
                          placement="bottom-start"
                          arrow
                          describeChild
                          disableInteractive
                          slotProps={{
                            popper: {
                              sx: {
                                "& .MuiTooltip-tooltip": {
                                  backgroundColor: "#1c1c1f",
                                  color: "#FFFFFF",
                                  margin: 0,
                                  fontSize: "12px",
                                  lineHeight: 1.3,
                                },
                                "& .MuiTooltip-arrow": {
                                  color: "#1c1c1f",
                                  margin: 0,
                                  left: "50% !important",
                                  transform: "translateX(-50%) !important",
                                },
                              },
                            },
                            tooltip: {
                              sx: {
                                margin: 0,
                                marginTop: "0px !important",
                                padding: "4px 8px",
                              },
                            },
                          }}
                        >
                          <span
                            style={{
                              fontSize: "11px",
                              maxWidth: "70px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "inline-block",
                              verticalAlign: "middle",
                            }}
                          >
                            {member.language || "English"}
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell sx={{ fontWeight: "500", fontSize: "16px" }}>
                  {member.dob}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "500", fontSize: "16px", width: 110 }}
                >
                  {(() => {
                    const v = member.average;
                    if (v === "N/A" || v === null || v === undefined)
                      return "N/A";
                    const str = String(v).trim();
                    if (/\d%$/.test(str)) return str;
                    const num = Number.parseFloat(str);
                    if (!Number.isNaN(num)) return `${num}%`;
                    return str;
                  })()}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "500", fontSize: "16px", width: 110 }}
                >
                  {member.attempts}
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "500", fontSize: "16px", width: 110 }}
                >
                  <ProgramList programs={member.programs || ""} />
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "500", fontSize: "16px", width: 110 }}
                >
                  <Tooltip
                    title={member.clientName || ""}
                    placement="bottom-start"
                    arrow
                    describeChild
                    disableInteractive
                    slotProps={{
                      popper: {
                        sx: {
                          "& .MuiTooltip-tooltip": {
                            backgroundColor: "#1c1c1f",
                            color: "#FFFFFF",
                            margin: 0,
                            fontSize: "12px",
                            lineHeight: 1.3,
                          },
                          "& .MuiTooltip-arrow": {
                            color: "#1c1c1f",
                            margin: 0,
                            left: "50% !important",
                            transform: "translateX(-50%) !important",
                          },
                        },
                      },
                      tooltip: {
                        sx: {
                          margin: 0,
                          marginTop: "0px !important",
                          padding: "4px 8px",
                        },
                      },
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 600,
                        fontSize: "16px",
                        lineHeight: "24px",
                        color: "#4b3f72",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "inline-block",
                        maxWidth: "100%",
                      }}
                    >
                      {member.clientName}
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

MemberReportingTable.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.string,
  results: PropTypes.array.isRequired,
  pageIndex: PropTypes.number.isRequired,
  totalRows: PropTypes.number.isRequired,
  computedTableHeight: PropTypes.number.isRequired,
  sortedMembers: PropTypes.array.isRequired,
  sortConfig: PropTypes.object.isRequired,
  onSort: PropTypes.func.isRequired,
  tableContainerRef: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

// --- Custom Hooks ---

const useMemberData = (appliedDateRange, pageSize = 30) => {
  const [membersReporting, setMembersReporting] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isFetchingMoreRef = useRef(false);
  const lastFetchSignatureRef = useRef(null);
  const { showLoading, hideLoading } = useLoading();

  const loadInitialData = useCallback(async () => {
    try {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const useCustom = appliedDateRange.startDate && appliedDateRange.endDate;
      const effectiveStart = useCustom
        ? appliedDateRange.startDate
        : startOfYear;
      const effectiveEnd = useCustom ? appliedDateRange.endDate : today;

      const params = {
        startDate: format(effectiveStart, "yyyy-MM-dd"),
        endDate: format(effectiveEnd, "yyyy-MM-dd"),
        page: pageIndex,
        size: pageSize,
      };
      setLoading(true);

      const signature = JSON.stringify({
        ...params,
        startDate: format(effectiveStart, "yyyy-MM-dd"),
        endDate: format(effectiveEnd, "yyyy-MM-dd"),
      });

      if (lastFetchSignatureRef.current === signature) {
        setLoading(false);
        return;
      }
      lastFetchSignatureRef.current = signature;

      showLoading();
      const apiRes = await fetchMemberReporting(params);
      setError(null);
      const mapped = mapResponse(apiRes);

      if (pageIndex === 0) {
        setResults(mapped);
        setMembersReporting(mapped);
      } else {
        setResults((prev) => [...prev, ...mapped]);
        setMembersReporting((prev) => [...prev, ...mapped]);
      }
      setTotalRows(apiRes.totalElements || mapped.length);
    } catch (err) {
      console.error(err);
      setError("Failed to load data");
    } finally {
      setLoading(false);
      isFetchingMoreRef.current = false;
      hideLoading();
    }
  }, [
    pageIndex,
    pageSize,
    appliedDateRange.startDate,
    appliedDateRange.endDate,
    showLoading,
    hideLoading,
  ]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    setPageIndex(0);
    setResults([]);
    setMembersReporting([]);
    lastFetchSignatureRef.current = null;
  }, [appliedDateRange]);

  return {
    results,
    loading,
    error,
    setError,
    totalRows,
    pageIndex,
    setPageIndex,
    membersReporting,
    isFetchingMoreRef,
  };
};

const useDateRangeFilter = () => {
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [appliedDateRange, setAppliedDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [endDateError, setEndDateError] = useState("");
  const [dateDialogWasApplied, setDateDialogWasApplied] = useState(false);

  const openDateDialog = () => {
    setDateDialogOpen(true);
    if (!dateDialogWasApplied) {
      setDateRange({ startDate: null, endDate: null });
      setEndDateError("");
    }
  };

  const closeDateDialog = () => {
    setDateDialogOpen(false);
    setDateRange({ startDate: null, endDate: null });
    setEndDateError("");
    setDateDialogWasApplied(false);
  };

  const handleDateChange = (date, type) => {
    setDateRange((prev) => ({ ...prev, [type]: date }));
  };

  const applyDateRange = () => {
    if (dateRange.startDate && dateRange.endDate && !endDateError) {
      setAppliedDateRange({ ...dateRange });
      setDateDialogWasApplied(true);
      setDateDialogOpen(false);
      return true;
    }
    return false;
  };

  return {
    dateRange,
    appliedDateRange,
    dateDialogOpen,
    endDateError,
    setEndDateError,
    openDateDialog,
    closeDateDialog,
    handleDateChange,
    applyDateRange,
    setAppliedDateRange,
    setDateRange,
  };
};

const useTableSort = (membersReporting) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const cycleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      if (prev.direction === "desc") return { key: null, direction: null };
      return { key, direction: "asc" };
    });
  };

  const sortedMembers = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return membersReporting;
    return getSortedData(membersReporting, sortConfig);
  }, [membersReporting, sortConfig]);

  return { sortConfig, cycleSort, sortedMembers };
};

const useTableLayout = (
  loading,
  isFetchingMoreRef,
  results,
  totalRows,
  setPageIndex,
) => {
  const tableContainerRef = useRef(null);
  const resultsContainerRef = useRef(null);
  const [computedTableHeight, setComputedTableHeight] = useState(0);
  const [containerScale, setContainerScale] = useState(1);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (loading || isFetchingMoreRef.current) return;
      if (results.length >= totalRows) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
        isFetchingMoreRef.current = true;
        setPageIndex((prev) => prev + 1);
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [loading, results.length, totalRows, setPageIndex, isFetchingMoreRef]);

  useEffect(() => {
    const computeHeight = () => {
      const el = resultsContainerRef.current;
      if (!el) return;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const top = el.getBoundingClientRect().top;
      const isSmallLaptop =
        window.innerWidth <= 1440 && window.innerHeight <= 900;
      const extra = isSmallLaptop ? 24 : 0;
      const bottomPadding = isSmallLaptop ? 4 : 16;
      setComputedTableHeight(
        Math.max(200, Math.floor(vh - top - bottomPadding + extra)),
      );
    };
    computeHeight();
    window.addEventListener("resize", computeHeight);
    const ro = new ResizeObserver(() => computeHeight());
    try {
      ro.observe(document.body);
    } catch (e) {
      console.log(e);
    }
    return () => {
      window.removeEventListener("resize", computeHeight);
      ro.disconnect?.();
    };
  }, []);

  useEffect(() => {
    const inches = getScreenInches();
    setContainerScale(inches < 14 ? 0.9 : 1);
  }, []);

  return {
    tableContainerRef,
    resultsContainerRef,
    computedTableHeight,
    containerScale,
  };
};

const MemberReporting = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    dateRange,
    appliedDateRange,
    dateDialogOpen,
    endDateError,
    setEndDateError,
    openDateDialog,
    closeDateDialog,
    handleDateChange,
    applyDateRange,
    setAppliedDateRange,
    setDateRange,
  } = useDateRangeFilter();

  const {
    results,
    loading,
    error,
    setError,
    totalRows,
    pageIndex,
    setPageIndex,
    membersReporting,
    isFetchingMoreRef,
  } = useMemberData(appliedDateRange);

  const { sortConfig, cycleSort, sortedMembers } =
    useTableSort(membersReporting);

  const {
    tableContainerRef,
    resultsContainerRef,
    computedTableHeight,
    containerScale,
  } = useTableLayout(
    loading,
    isFetchingMoreRef,
    results,
    totalRows,
    setPageIndex,
  );

  const location = useLocation();
  const isPriorityOutreach = location.pathname.includes("priority-outreach");
  const isMemberMonitoring = location.pathname.includes("monitoring");

  const getCurrentSection = () => {
    if (isPriorityOutreach) return "priority-outreach";
    if (isMemberMonitoring) return "monitoring";
    return "members";
  };
  const [activeSection] = useState(getCurrentSection());
  const currentSection = activeSection ? SECTIONS[activeSection] : null;
  const sectionTitle = currentSection?.title || "";
  const sectionDescription = currentSection?.description || "";

  return (
    <Box sx={{ display: "flex", backgroundColor: "transparent" }}>
      <CssBaseline />

      <MainContent className="search-page">
        {dateDialogOpen && (
          <Box
            onClick={closeDateDialog}
            sx={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.35)",
              zIndex: 1200,
            }}
          />
        )}
        <div className="search-page-container">
          <div
            className="search-page-content"
            style={{
              transform: `scale(${containerScale})`,
              transformOrigin: "top left",
              width: containerScale < 1 ? "111%" : "100%",
              minHeight: "92vh",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minHeight: "55px",
                ml: 0,
                mt: 1,
                marginBottom: "-8px",
                fontSize: "18px",
                fontWeight: 600,
                lineHeight: "30px",
                letterSpacing: "0%",
                color: "#080A0E",
                "& a, & span:last-child": {
                  color: "#080A0E",
                  fontSize: "20px",
                  fontWeight: 600,
                  lineHeight: "30px",
                  letterSpacing: "0%",
                  textDecoration: "none",
                },
                "& a:hover": {
                  textDecoration: "underline",
                },
                "& .breadcrumb-separator": {
                  margin: "0 4px",
                  color: "#080A0E",
                },
              }}
            >
              {BREADCRUMBS.map((breadcrumb, index) => (
                <React.Fragment
                  key={`breadcrumb-${breadcrumb.label || ""}-${breadcrumb.path || ""}-${index}`}
                >
                  {index > 0 && (
                    <span className="breadcrumb-separator">&gt;</span>
                  )}
                  {breadcrumb.path ? (
                    <a href={breadcrumb.path}>{breadcrumb.label}</a>
                  ) : (
                    <span>{breadcrumb.label}</span>
                  )}
                </React.Fragment>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <PageHeader
                  title={sectionTitle}
                  description={sectionDescription}
                />
              </Box>
              <Stack direction="row" spacing={1} marginRight={1}>
                <MemberExportButton
                  totalRows={totalRows}
                  appliedDateRange={appliedDateRange}
                  setError={setError}
                />
                <MemberDateFilterControl
                  appliedDateRange={appliedDateRange}
                  openDateDialog={openDateDialog}
                  closeDateDialog={closeDateDialog}
                  setAppliedDateRange={setAppliedDateRange}
                  setDateRange={setDateRange}
                  setPageIndex={setPageIndex}
                  totalRows={totalRows}
                />
                <MemberDateRangeDialog
                  open={dateDialogOpen}
                  onClose={closeDateDialog}
                  dateRange={dateRange}
                  onDateChange={handleDateChange}
                  endDateError={endDateError}
                  setEndDateError={setEndDateError}
                  onApply={() => {
                    if (applyDateRange()) {
                      setPageIndex(0);
                    }
                  }}
                />
              </Stack>
            </Box>

            <div className="search-results-container" ref={resultsContainerRef}>
              <MemberReportingTable
                loading={loading}
                error={error}
                results={results}
                pageIndex={pageIndex}
                totalRows={totalRows}
                computedTableHeight={computedTableHeight}
                sortedMembers={sortedMembers}
                sortConfig={sortConfig}
                onSort={cycleSort}
                tableContainerRef={tableContainerRef}
                isMobile={isMobile}
              />
            </div>
          </div>
        </div>
      </MainContent>
    </Box>
  );
};

export default MemberReporting;
